/**
 * The one container treatment every sidepanel list item shares: nothing at rest,
 * and on hover a hairline outline, a barely-lifted fill and a soft shadow
 * (Figma `6972:11353`). Actions and reminders already read this way; this is
 * what makes history rows, meeting rows, meeting follow-ups and the section CTAs
 * read the same.
 *
 * Written to suit both kinds of row it lands on:
 * - a `Cell`, where it also has to strip the primitive's own fill, shadow and
 *   gradient inset (`before:`) back to nothing at rest;
 * - a plain `div`/`button`, where the `before:` and `shadow-none` resets are
 *   inert and the border is the only thing doing work.
 *
 * The border is present-but-transparent at rest rather than added on hover, so
 * hovering never resizes a row. Figma draws the same hairline as an inside
 * stroke, which is the same idea.
 *
 * The hover treatment also keys off `group-hover/action` — the wrapper the
 * hover-button cluster hangs from. The cluster is a *sibling* of the cell and
 * overhangs its corner, so pointer-on-cluster leaves the cell's own `:hover`
 * but must not drop its hovered look. Inert for hosts without that wrapper.
 *
 * `group-data-[popup-open]/action` covers the third pointer home: the hover
 * preview card. It portals outside the row entirely, so neither `:hover` nor
 * the group hover sees the pointer there — but Base UI stamps the trigger
 * wrapper with `data-popup-open` for as long as the card is up.
 *
 * No hover fades here or in the row's children (title colour, chevron,
 * close, section chrome): the overlay is a full-screen transparent window, so
 * each frame of a 150ms fade repaints the panel surface. Sweeping the pointer
 * down a list measured ~25% GPU with the fades and ~8% without; keeping only
 * the fade-out still measured ~15%, because leave-fades overlap the next
 * row's hover and the panel repaints on most frames anyway.
 */
export const SIDEPANEL_CELL_SURFACE =
  'rounded-xl corner-smooth border border-transparent bg-transparent shadow-none focus-visible:focus-ring before:bg-transparent dark:before:bg-transparent transition-opacity duration-150 hover:border-border hover:bg-background/30 hover:shadow-sm group-hover/action:border-border group-hover/action:bg-background/30 group-hover/action:shadow-sm group-data-[popup-open]/action:border-border group-data-[popup-open]/action:bg-background/30 group-data-[popup-open]/action:shadow-sm'

/**
 * Goes after `SIDEPANEL_CELL_SURFACE` for the calendar rows' hover, which the To-dos rows share:
 * a grey fill instead of the lifted outline, and while the row's popover is up it keeps the fill
 * and adds the outline. A row that isn't its popover's trigger stamps `data-popup-open` itself.
 * Tailwind emits `data-*` after `hover`, so the open row's outline wins under the pointer.
 *
 * Only for a row with no hover cluster, whose wrapper is no `group/action`: the surface's
 * `group-hover/action:` outline outranks a plain `hover:` and would come back under the pointer.
 *
 * `after:hidden` drops a clickable `Cell`'s own hover lift, a shadow on `::after` that a calendar
 * row, being no `Cell`, never has.
 *
 * The open row's outline is its focus state too, so it drops the focus ring the arrow keys would
 * otherwise draw around it.
 */
export const SIDEPANEL_FILLED_HOVER_SURFACE =
  'after:hidden hover:bg-element hover:border-transparent hover:shadow-none data-[popup-open]:border-border data-[popup-open]:bg-element data-[popup-open]:shadow-sm data-[popup-open]:outline-none'

export const SIDEPANEL_CELL_TITLE = 'text-foreground line-clamp-2 font-normal'

/**
 * The title of a row that opens in a full view rather than a popover: underlined in grey while its
 * row, a `group/cell` or a `group/row`, is hovered or focused.
 */
export const OPENS_IN_VIEW_TITLE =
  'underline decoration-transparent underline-offset-2 group-hover/cell:decoration-foreground-placeholder group-focus-visible/cell:decoration-foreground-placeholder group-hover/row:decoration-foreground-placeholder group-focus-visible/row:decoration-foreground-placeholder'

/**
 * A To-dos row's mark: drawn at 14px, centred in the 16px slot the titles, indents and nested rows
 * are set from. A row tops it with `mt-[3px]` to centre it on the title's 20px first line.
 */
export const TODO_MARK = 'mx-px [--todo-h:14px]'

/** A To-dos row's title type, which every row on the To-dos Home shares. */
export const TODO_TITLE_TYPE = 'text-md'

/** A To-dos row's title: one line that fades out over its last 24px instead of ending in an ellipsis. */
export const TODO_TITLE = `${TODO_TITLE_TYPE} overflow-hidden whitespace-nowrap [-webkit-mask-image:linear-gradient(to_right,black_calc(100%_-_24px),transparent)] [mask-image:linear-gradient(to_right,black_calc(100%_-_24px),transparent)]`
